import { CameraControls, PerspectiveCamera } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { BundleStore, useBundleStore } from '../hooks/bundle-store';
import { useShallow } from 'zustand/react/shallow';
import { HIDDEN_BACK_SIDE_NAME, RESET_CAMERA } from '../constants';

export default function Camera({ canvasParent, cameraControlRef, modelsRef, enableZoom }: any) {
  const { scene } = useThree();
  const [dimensions, setDimensions] = useState<{ x: number; y: number; z: number } | undefined>();

  const light = useRef(null);
  const isSetDefaultCameraPosition = useRef(false);
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const { isSingle, focusedSurface, shouldFocusSurface, isApparel } = useBundleStore(
    useShallow((state: BundleStore) => ({
      isSingle: state?.bundle?.isSingle,
      focusedSurface: state.focusedSurface,
      shouldFocusSurface: state.shouldFocusSurface,
      isApparel: state.bundle?.isApparel,
    })),
  );

  const handleReset3dView = () => {
    cameraControlRef.current.rotatePolarTo(Math.PI / 2.5, true);
    cameraControlRef.current.rotateAzimuthTo(Math.PI / 3, true);
    cameraControlRef.current.fitToSphere(modelsRef.current, true).then(() => {
      if (cameraRef.current) {
        if (!isSingle && !isApparel) {
          cameraControlRef.current.fitToSphere(modelsRef.current, true);

          const boundingBox = computeBoundingBox(modelsRef.current);
          const height = boundingBox.max.y - boundingBox.min.y;
          const offsetY = (height * 10) / 100;

          const target = new THREE.Vector3();
          cameraControlRef.current.getTarget(target);

          target.y -= offsetY;
          cameraControlRef.current.moveTo(target.x, target.y, target.z, true);
          cameraControlRef.current.dolly(enableZoom ? 1.3 : 0.6, true);
        }
      }
    });
  };

  useEffect(() => {
    if (cameraRef.current && cameraControlRef.current) {
      if (!isSetDefaultCameraPosition.current) {
        setTimeout(() => {
          handleReset3dView();

          const models = scene.getObjectByName('models');
          const boundingBox = computeBoundingBox(models as THREE.Group);
          const height = boundingBox.max.y - boundingBox.min.y;
          const width = boundingBox.max.x - boundingBox.min.x;
          const depth = boundingBox.max.z - boundingBox.min.z;

          if (cameraRef?.current && enableZoom) {
            const box = new THREE.Box3().setFromObject(models as THREE.Object3D);
            const sphere = new THREE.Sphere();
            box.getBoundingSphere(sphere); // gives center and radius

            const camera = cameraRef.current;

            const fovY = (camera.fov * Math.PI) / 180;
            const aspect = camera.aspect;

            // 3. Compute horizontal FOV
            const fovX = 2 * Math.atan(Math.tan(fovY / 2) * aspect);

            // 4. Required distances to fully fit the sphere in both directions
            const distanceY = sphere.radius / Math.sin(fovY / 2);
            const distanceX = sphere.radius / Math.sin(fovX / 2);

            // Final distance is max of both
            const requiredDistance = Math.max(distanceX, distanceY);

            // 5. Apply some padding
            const paddedDistance = requiredDistance;

            // 6. Update camera and controls
            camera.far = paddedDistance * 2;
            camera.updateProjectionMatrix();
            cameraControlRef.current.maxDistance = paddedDistance;
          }

          if (isSingle) {
            if (!isApparel) {
              //@ts-expect-error
              light.current.position.copy(new THREE.Vector3(0, height * 1.4, 0));
            }
          } else {
            let x = -(depth / 4);
            let y = height * 1.7;
            const z = width;

            if (isSingle) {
              const finalHight = depth > height ? depth : height;
              const finalDepth = height > depth ? depth : height;
              x = -(finalDepth / 4);
              y = finalHight * 1.7;
            }
            if (!isApparel) {
              //@ts-expect-error
              light?.current?.position?.copy(new THREE.Vector3(x, y, z));
            }
            setDimensions({
              x,
              y,
              z,
            });
          }
        }, 100);
        isSetDefaultCameraPosition.current = true;
      } else {
        if (focusedSurface === RESET_CAMERA) {
          handleReset3dView();
        } else {
          const surfaceId = focusedSurface?.split('_2ND_');
          const isTentOutsideWall = surfaceId?.[2] === 'BACK';
          const name = (surfaceId?.[0] as string) + '_2ND_' + surfaceId?.[1];

          const surfaceNode = scene.getObjectByName(name);

          const childNode = surfaceNode?.children?.[0];
          const finalSurfaceNode =
            isTentOutsideWall || childNode?.name?.includes(HIDDEN_BACK_SIDE_NAME)
              ? childNode
              : surfaceNode;

          //@ts-expect-error isMesh is exist
          if (finalSurfaceNode && finalSurfaceNode?.isMesh) {
            finalSurfaceNode.visible = true;
            const meshWorldPosition = new THREE.Vector3();
            finalSurfaceNode.getWorldPosition(meshWorldPosition);
            const center = getCenterPoint(finalSurfaceNode);
            cameraControlRef.current.setLookAt(
              -meshWorldPosition.x,
              meshWorldPosition.y,
              -meshWorldPosition.z,
              -center.x,
              center.y,
              -center.z,
              true,
            );
            cameraControlRef.current.rotatePolarTo(Math.PI / 2.5, true);
            cameraControlRef.current.fitToSphere(finalSurfaceNode, true);
          }
        }
      }
    }

    window.addEventListener('reset3dView', handleReset3dView);
    return () => {
      window.removeEventListener('reset3dView', handleReset3dView);
    };
  }, [isSingle, shouldFocusSurface, enableZoom]);

  useFrame(({ camera }) => {
    if (!isApparel && light.current && dimensions?.x) {
      let rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(camera.rotation);
      let lightPosition = new THREE.Vector3(
        dimensions?.x,
        dimensions?.y,
        dimensions?.z,
      ).applyMatrix4(rotationMatrix);
      //@ts-expect-error
      light?.current?.position.copy(lightPosition);
    }
  });

  return (
    <>
      <CameraControls
        makeDefault
        ref={cameraControlRef}
        smoothTime={0.25}
        maxDistance={200}
        mouseButtons={enableZoom ? undefined : { wheel: 0, left: 1, right: 2, middle: 4 }}
        touches={enableZoom ? undefined : { one: 32, two: 0, three: 1024 }}
      />
      <PerspectiveCamera
        ref={cameraRef}
        makeDefault
        fov={37}
        aspect={
          canvasParent
            ? canvasParent?.clientWidth
            : 0 / (canvasParent ? canvasParent?.clientHeight : 0)
        }
        near={0.04}
        far={205}
      />
      {!isApparel && (
        <directionalLight ref={light} intensity={1} castShadow target={modelsRef.current} />
      )}
    </>
  );
}

export function getModelScreenRect(
  object: THREE.Object3D,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  const box = new THREE.Box3().setFromObject(object);
  const points = [
    new THREE.Vector3(box.min.x, box.min.y, box.min.z),
    new THREE.Vector3(box.min.x, box.min.y, box.max.z),
    new THREE.Vector3(box.min.x, box.max.y, box.min.z),
    new THREE.Vector3(box.min.x, box.max.y, box.max.z),
    new THREE.Vector3(box.max.x, box.min.y, box.min.z),
    new THREE.Vector3(box.max.x, box.min.y, box.max.z),
    new THREE.Vector3(box.max.x, box.max.y, box.min.z),
    new THREE.Vector3(box.max.x, box.max.y, box.max.z),
  ];

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  points.forEach((pt) => {
    pt.project(camera);
    const x = ((pt.x + 1) / 2) * canvas.width;
    const y = ((-pt.y + 1) / 2) * canvas.height;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function screenDeltaToWorld(
  dx: number,
  dy: number,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  const ndcDx = (dx / canvas.width) * 2;
  const ndcDy = -(dy / canvas.height) * 2;

  const p1 = new THREE.Vector3(ndcDx, ndcDy, 0).unproject(camera);
  const p0 = new THREE.Vector3(0, 0, 0).unproject(camera);

  return p1.sub(p0);
}

export function fitModelToView(
  object: THREE.Object3D,
  camera: THREE.Camera,
  gl: THREE.WebGLRenderer,
  cameraControls: CameraControls,
) {
  if (!object || !camera || !gl) return;

  const canvas = gl.domElement;
  const rect = getModelScreenRect(object, camera, canvas);

  // --- Centering
  const modelCenterX = rect.x + rect.width / 2;
  const modelCenterY = rect.y + rect.height / 2;

  const canvasCenterX = canvas.width / 2;
  const canvasCenterY = canvas.height / 2;

  const dx = canvasCenterX - modelCenterX;
  const dy = canvasCenterY - modelCenterY;

  const delta = screenDeltaToWorld(dx, dy, camera, canvas);

  const target = new THREE.Vector3();
  cameraControls.getTarget(target);
  target.add(delta);
  cameraControls.moveTo(target.x, target.y, target.z, true);

  // --- Scaling
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const scale = Math.min(scaleX, scaleY);

  if (scale !== Infinity && scale > 0) {
    cameraControls.dolly(scale, true);
  }
}

export const computeBoundingBox = (group: THREE.Group) => {
  const boundingBox = new THREE.Box3();

  group.traverse((object: any) => {
    if (object?.isMesh) {
      object.geometry.computeBoundingBox();
      boundingBox.union(object.geometry.boundingBox.clone().applyMatrix4(object.matrixWorld));
    }
  });

  return boundingBox;
};

function getCenterPoint(mesh: any) {
  const geometry = mesh.geometry;
  geometry.computeBoundingBox();
  const center = new THREE.Vector3();
  geometry.boundingBox.getCenter(center);
  mesh.localToWorld(center);
  return center;
}
